import { useMemo, useState, type ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useMutation } from '@tanstack/react-query';
import { ArrowDown, Car, Check, CircleDot, Plus } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { miles, minutes } from '../lib/format';
import { load, save } from '../lib/storage';
import { kindForStops, useGroup } from '../lib/places';
import { endsFromWaypoints, finishLabel } from '../lib/tripEnds';
import type { AreaResult, PlannedRoute, Waypoint } from '../lib/types';
import { useTrip } from '../state/trip';
import PlaceMap from '../components/PlaceMap';
import PlaceCard from '../components/PlaceCard';
import PlaceSearch from '../components/PlaceSearch';
import { DaySelect, GroupChips, ScoreSlider, TagToggles } from '../components/Filters';
import KindChip from '../components/KindChip';
import { Empty, ErrorNote, PageHeader } from '../components/ui';

type Mode = 'route' | 'loop';

interface SavedInputs {
  start: Waypoint | null;
  end: Waypoint | null;
  via: (Waypoint | null)[];
  center: Waypoint | null;
  corridor: number;
  radius: number;
  loopStops: number;
  minScore: number;
  tags: string[];
}

const DEFAULTS: SavedInputs = {
  start: null,
  end: null,
  via: [],
  center: null,
  corridor: 10,
  radius: 25,
  loopStops: 5,
  minScore: 50,
  tags: [],
};

const INPUTS_KEY = 'bbh.planner.v1';

export default function PlannerPage() {
  const [params, setParams] = useSearchParams();
  const mode: Mode = params.get('mode') === 'loop' ? 'loop' : 'route';
  const setMode = (m: Mode) => setParams(m === 'loop' ? { mode: 'loop' } : {}, { replace: true });

  const [inputs, setInputs] = useState<SavedInputs>(() => ({ ...DEFAULTS, ...load<Partial<SavedInputs>>(INPUTS_KEY, {}) }));
  const [day, setDay] = useState<number | ''>('');
  const [group, setGroup] = useGroup();
  const set = (patch: Partial<SavedInputs>) =>
    setInputs((i) => {
      const next = { ...i, ...patch };
      save(INPUTS_KEY, next);
      return next;
    });

  const filters = { group, minScore: inputs.minScore, tags: inputs.tags, openOnDay: day === '' ? undefined : day };

  const { trip, setEnds } = useTrip();
  const routePlan = useMutation({
    mutationFn: (waypoints: Waypoint[]) =>
      api.planRoute({ waypoints, corridorMiles: inputs.corridor, maxResults: 300, ...filters }),
    // The drive's start and end become the trip's, unless the trip already has its own.
    onSuccess: (plan) => {
      if (!trip.start && !trip.end && !trip.endAtStart) setEnds(routeEnds(plan));
    },
  });

  const areaPlan = useMutation({
    mutationFn: (c: Waypoint) =>
      api.exploreArea({ lat: c.lat, lng: c.lng, radiusMiles: inputs.radius, loopStops: inputs.loopStops, maxResults: 250, ...filters }),
  });

  const waypoints = [inputs.start, ...inputs.via, inputs.end];
  const routeReady = !!inputs.start && !!inputs.end && inputs.via.every(Boolean);

  return (
    <>
      <PageHeader eyebrow="Trip planner" title="Plan a trip">
        Find backroad bars and bites along a drive, or around wherever you&apos;re staying. Add your favorites to your
        trip; a bar hop also gets a named, sober driver.
      </PageHeader>

      <div className="mx-auto max-w-7xl px-4 pb-14 sm:px-6">
        <div role="tablist" aria-label="Planner mode" className="mb-5 inline-flex rounded-full border border-line-2 p-1">
          <TabButton active={mode === 'route'} onClick={() => setMode('route')} icon={<Car size={16} aria-hidden />}>
            Road trip
          </TabButton>
          <TabButton active={mode === 'loop'} onClick={() => setMode('loop')} icon={<CircleDot size={16} aria-hidden />}>
            Around town
          </TabButton>
        </div>

        <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
          <form
            className="card flex h-fit flex-col gap-5 p-5 lg:sticky lg:top-24"
            onSubmit={(e) => {
              e.preventDefault();
              if (mode === 'route' && routeReady) routePlan.mutate(waypoints as Waypoint[]);
              if (mode === 'loop' && inputs.center) areaPlan.mutate(inputs.center);
            }}
          >
            {mode === 'route' ? (
              <>
                <PlaceSearch label="Starting from" value={inputs.start} onChange={(w) => set({ start: w })} placeholder="e.g. Gainesville, FL" locate />
                {inputs.via.map((v, i) => (
                  <PlaceSearch
                    key={i}
                    label={`Stop ${i + 1} (overnight or detour)`}
                    value={v}
                    placeholder="e.g. Chattanooga, TN"
                    onChange={(w) => set({ via: inputs.via.map((x, j) => (j === i ? w : x)) })}
                    onRemove={() => set({ via: inputs.via.filter((_, j) => j !== i) })}
                  />
                ))}
                {inputs.via.length < 8 && (
                  <button type="button" className="btn btn-sm self-start" onClick={() => set({ via: [...inputs.via, null] })}>
                    <Plus size={16} aria-hidden /> Add a stop along the way
                  </button>
                )}
                <PlaceSearch label="Ending at" value={inputs.end} onChange={(w) => set({ end: w })} placeholder="e.g. Manistee, MI" />

                <div className="flex flex-col gap-1.5">
                  <label htmlFor="corridor">
                    Up to <span className="tabular-nums text-amber">{inputs.corridor} miles</span> off the route
                  </label>
                  <input
                    id="corridor"
                    type="range"
                    min={1}
                    max={25}
                    value={inputs.corridor}
                    onChange={(e) => set({ corridor: Number(e.target.value) })}
                  />
                </div>
              </>
            ) : (
              <>
                <PlaceSearch label="Where you're staying" value={inputs.center} onChange={(w) => set({ center: w })} placeholder="e.g. Chattanooga, TN" locate />
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="radius">
                    Within <span className="tabular-nums text-amber">{inputs.radius} miles</span>
                  </label>
                  <input id="radius" type="range" min={5} max={100} step={5} value={inputs.radius} onChange={(e) => set({ radius: Number(e.target.value) })} />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label htmlFor="loopStops">
                    Suggested loop: <span className="tabular-nums text-amber">{inputs.loopStops} stops</span>
                  </label>
                  <input id="loopStops" type="range" min={2} max={8} value={inputs.loopStops} onChange={(e) => set({ loopStops: Number(e.target.value) })} />
                </div>
              </>
            )}

            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Stop for</span>
              <GroupChips value={group} onChange={setGroup} label="Stop for" className="self-start" />
            </div>
            <ScoreSlider value={inputs.minScore} onChange={(n) => set({ minScore: n })} />
            <DaySelect value={day} onChange={setDay} />
            <div className="flex flex-col gap-1.5">
              <span className="text-sm font-semibold">Looking for</span>
              <TagToggles value={inputs.tags} onChange={(t) => set({ tags: t })} />
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={mode === 'route' ? !routeReady || routePlan.isPending : !inputs.center || areaPlan.isPending}
            >
              {(mode === 'route' ? routePlan.isPending : areaPlan.isPending) ? 'Finding stops…' : 'Find stops'}
            </button>
            {mode === 'route' && !routeReady && <p className="text-[13px] text-dim">Pick a start and an end from the suggestions.</p>}
          </form>

          <div className="min-w-0">
            {mode === 'route' ? (
              <RouteResults plan={routePlan.data} error={routePlan.error} pending={routePlan.isPending} day={day} />
            ) : (
              <LoopResults result={areaPlan.data} center={inputs.center} error={areaPlan.error} pending={areaPlan.isPending} day={day} />
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function TabButton({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon: ReactNode; children: ReactNode }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={`inline-flex min-h-10 items-center gap-2 rounded-full px-4 font-semibold ${active ? 'bg-amber text-amber-ink' : 'text-cream hover:bg-panel'}`}
    >
      {icon}
      {children}
    </button>
  );
}

/** A planned drive's first and last points as the trip's start and finish. */
function routeEnds(plan: PlannedRoute) {
  return endsFromWaypoints([plan.waypoints[0], plan.waypoints[plan.waypoints.length - 1]]);
}

function RouteResults({ plan, error, pending, day }: { plan?: PlannedRoute; error: Error | null; pending: boolean; day: number | '' }) {
  const { trip, setEnds } = useTrip();
  const [selected, setSelected] = useState<string | null>(null);

  const legs = useMemo(() => {
    if (!plan) return [];
    return plan.waypoints.slice(0, -1).map((w, i) => ({
      from: w,
      to: plan.waypoints[i + 1],
      places: plan.places.filter((p) => p.segmentIndex === i),
    }));
  }, [plan]);

  if (error) return <ErrorNote error={error}>{error instanceof ApiError ? error.message : 'Could not plan that route.'}</ErrorNote>;
  if (!plan)
    return (
      <Empty>
        {pending ? 'Getting directions and finding stops along the way…' : 'Enter a start and an end to see the bars and bites along your drive.'}
      </Empty>
    );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 tabular-nums">
        <span className="font-display text-3xl font-black uppercase">{miles(plan.totalMiles)}</span>
        <span className="text-muted">~{minutes(plan.totalMinutes)} of driving</span>
        <span className="text-muted">
          {plan.totalMatches.toLocaleString()} stops within {plan.corridorMiles} mi
          {plan.totalMatches > plan.places.length ? ` · showing the best ${plan.places.length}` : ''}
        </span>
      </div>
      {plan.isEstimate && (
        <ErrorNote>Directions weren&apos;t available, so this uses straight lines between your stops. Results are approximate.</ErrorNote>
      )}

      <PlaceMap
        places={plan.places.map((c) => c.place)}
        lines={[plan.line]}
        waypoints={plan.waypoints}
        fitPoints={plan.line}
        fitKey={plan.line.length + ':' + plan.totalMiles}
        selectedId={selected}
        onSelect={setSelected}
        className="h-[460px]"
        label="Map of your route and the stops along it"
      />

      <TripEndsFromPlan
        current={{ start: trip.start?.label ?? null, finish: finishLabel(trip) }}
        planned={{ start: plan.waypoints[0].label, finish: finishLabel(routeEnds(plan)) ?? '' }}
        onUse={() => setEnds(routeEnds(plan))}
      />

      {legs.map((leg, i) => (
        <section key={i} aria-labelledby={`leg-${i}`} className="flex flex-col gap-3">
          <h2 id={`leg-${i}`} className="flex flex-wrap items-center gap-2 pt-3 text-3xl font-black">
            <span className="rounded-md bg-cream px-2 text-asphalt">{String.fromCharCode(65 + i)}</span>
            {leg.from.label}
            <ArrowDown size={22} className="-rotate-90 text-amber" aria-hidden />
            <span className="rounded-md bg-cream px-2 text-asphalt">{String.fromCharCode(66 + i)}</span>
            {leg.to.label}
          </h2>
          <p className="text-sm text-muted tabular-nums">
            Miles {Math.round(leg.from.milesFromStart)}–{Math.round(leg.to.milesFromStart)} · {leg.places.length} stops
          </p>
          {leg.places.length === 0 ? (
            <Empty>Nothing on this stretch with your filters. Try widening the distance off route or lowering the score.</Empty>
          ) : (
            <div className="grid gap-3 xl:grid-cols-2">
              {leg.places.map((c) => (
                <PlaceCard
                  key={c.place.id}
                  place={c.place}
                  addAs="RoadTrip"
                  day={day === '' ? undefined : day}
                  meta={`Mile ${Math.round(c.milesFromStart)} · ${c.offRouteMiles < 0.5 ? 'right on the route' : `${c.offRouteMiles} mi off route`}`}
                  selected={selected === c.place.id}
                  onSelect={() => setSelected(c.place.id)}
                  compact
                />
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function TripEndsFromPlan({
  current,
  planned,
  onUse,
}: {
  current: { start: string | null; finish: string | null };
  planned: { start: string; finish: string };
  onUse: () => void;
}) {
  if (current.start === planned.start && current.finish === planned.finish) {
    return (
      <p className="flex items-center gap-2 text-sm text-ok">
        <Check size={16} aria-hidden /> Your trip starts at {planned.start} and finishes at {planned.finish}. Add stops and they go in between.
      </p>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm text-muted">
      {current.start || current.finish ? (
        <span>
          Your trip runs from {current.start ?? 'wherever you are'} to {current.finish ?? 'the last stop'}.
        </span>
      ) : null}
      <button type="button" className="btn btn-sm" onClick={onUse}>
        Start my trip at {planned.start}, finish at {planned.finish}
      </button>
    </div>
  );
}

function LoopResults({
  result,
  center,
  error,
  pending,
  day,
}: {
  result?: AreaResult;
  center: Waypoint | null;
  error: Error | null;
  pending: boolean;
  day: number | '';
}) {
  const { replaceStops } = useTrip();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(null);

  if (error) return <ErrorNote error={error} />;
  if (!result)
    return <Empty>{pending ? 'Looking around…' : 'Pick where you’re staying to see the bars and bites nearby, and a suggested loop.'}</Empty>;

  const loop = result.suggestedLoop;
  const loopIds = new Set(loop?.stops.map((s) => s.id));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-1 tabular-nums">
        <span className="font-display text-3xl font-black uppercase">{result.totalMatches.toLocaleString()} stops</span>
        <span className="text-muted">within {result.radiusMiles} miles of {center?.label ?? 'your spot'}</span>
      </div>

      <PlaceMap
        places={result.places}
        stops={loop?.stops ?? []}
        lines={loop ? [loop.line] : []}
        waypoints={center ? [center] : []}
        circle={{ lat: result.centerLat, lng: result.centerLng, radiusMiles: result.radiusMiles }}
        fitPoints={[
          [result.centerLat + result.radiusMiles / 69, result.centerLng],
          [result.centerLat - result.radiusMiles / 69, result.centerLng],
        ]}
        fitKey={`${result.centerLat},${result.centerLng},${result.radiusMiles}`}
        selectedId={selected}
        onSelect={setSelected}
        className="h-[460px]"
        label="Map of the stops around your spot"
      />

      {loop && (
        <section className="card flex flex-col gap-3 border-amber/60 p-5" aria-labelledby="loopH">
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div>
              <h2 id="loopH" className="text-3xl font-black">Suggested loop</h2>
              <p className="text-sm text-muted tabular-nums">
                {loop.stops.length} stops · {miles(loop.miles)} · ~{minutes(loop.minutes)} driving, out and back
                {loop.isEstimate ? ' (estimated)' : ''}
              </p>
            </div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => {
                replaceStops(loop.stops, center ? { ...endsFromWaypoints([center]), endAtStart: true } : undefined, kindForStops);
                navigate('/my-trip');
              }}
            >
              Use this loop for my trip
            </button>
          </div>
          <ol className="flex flex-col gap-1">
            {loop.stops.map((s, i) => (
              <li key={s.id} className="flex items-center gap-3">
                <span className="flex size-7 items-center justify-center rounded-full bg-amber text-sm font-extrabold text-amber-ink">{i + 1}</span>
                <span className="font-semibold">{s.name}</span>
                <KindChip kind={s.kind} />
                <span className="text-sm text-muted">{[s.town, s.state].filter(Boolean).join(', ')}</span>
              </li>
            ))}
          </ol>
        </section>
      )}

      <h2 className="pt-2 text-3xl font-black">Everything nearby</h2>
      {result.places.length === 0 ? (
        <Empty>Nothing found. Try a bigger radius, a lower backroad score, or both bars and bites.</Empty>
      ) : (
        <div className="grid gap-3 xl:grid-cols-2">
          {result.places
            .filter((b) => !loopIds.has(b.id))
            .slice(0, 80)
            .map((b) => (
              <PlaceCard
                key={b.id}
                place={b}
                addAs={kindForStops}
                day={day === '' ? undefined : day}
                selected={selected === b.id}
                onSelect={() => setSelected(b.id)}
                compact
              />
            ))}
        </div>
      )}
    </div>
  );
}
